import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Box, Skeleton } from "@mui/material";

import { useDispatch, useSelector } from "react-redux";

import { toast } from "react-toastify";

import { fetchAccessibleGroups } from "../../../../../store/features/groupsApi";

import {

  clearBusinessEmail,

  clearBusinessEmailError,

  deleteBusinessEmail,

  fetchBusinessEmail,

  patchBusinessEmail,

} from "../../../../../store/features/businessEmailApi";

import { fetchGroupMailDomainStatus } from "../../../../../store/features/mailDomainAuthApi";

import SettingsContent from "../../shared/SettingsContent";

import SettingsPageHeader from "../../shared/SettingsPageHeader";

import DomainBanner from "../../LinkEmail/DomainBanner";

import BusinessEmailHierarchy from "../BusinessEmail/BusinessEmailHierarchy";

import BusinessEmailInfoCard from "../BusinessEmail/BusinessEmailInfoCard";

import BusinessEmailVerifiedCard from "../BusinessEmail/BusinessEmailVerifiedCard";

import { getErrorMessage } from "../BusinessEmail/constants";

import BusinessEmailGroupSelect from "../BusinessEmail/BusinessEmailGroupSelect";

import { getActiveGroups, getDefaultGroupId } from "../../shared/groupUtils";



function BusinessEmailPage() {

  const dispatch = useDispatch();

  const { list: groups, listLoading } = useSelector((state) => state.peopleGroups);

  const {

    business_email: groupBusinessEmail,

    hierarchy,

    loading: groupEmailLoading,

    saving,

    deleting,

    error: groupEmailError,

    loaded: groupEmailLoaded,

    groupId: loadedGroupId,

  } = useSelector((state) => state.businessEmail);

  const { statusData, statusLoading } = useSelector((state) => state.mailDomainAuthApi);



  const [selectedGroupId, setSelectedGroupId] = useState("");

  const [emailInput, setEmailInput] = useState("");



  const activeGroups = useMemo(() => getActiveGroups(groups), [groups]);

  const defaultGroupId = useMemo(() => getDefaultGroupId(groups), [groups]);

  const resolvedGroupId = selectedGroupId || defaultGroupId;



  const domainVerified = statusData?.mail_domain_verified === true;

  const domainStatusReady = statusData != null;



  const selectedGroup = useMemo(

    () => groups.find((group) => group.id === resolvedGroupId),

    [groups, resolvedGroupId],

  );



  const groupName = selectedGroup?.name || "this group";

  const isLinked = Boolean(groupBusinessEmail?.trim());

  const isGroupDataReady =

    !resolvedGroupId || (groupEmailLoaded && loadedGroupId === resolvedGroupId);



  useEffect(() => {

    dispatch(fetchAccessibleGroups());

    return () => {

      dispatch(clearBusinessEmail());

    };

  }, [dispatch]);



  const loadGroupData = useCallback(

    (groupId) => {

      if (!groupId) return;

      dispatch(fetchBusinessEmail({ groupId }));

      dispatch(fetchGroupMailDomainStatus({ groupId }));

    },

    [dispatch],

  );



  useEffect(() => {

    if (resolvedGroupId) {

      loadGroupData(resolvedGroupId);

    }

  }, [resolvedGroupId, loadGroupData]);



  useEffect(() => {

    if (!resolvedGroupId || !groupEmailLoaded || loadedGroupId !== resolvedGroupId) return;

    setEmailInput(groupBusinessEmail || "");

  }, [resolvedGroupId, groupEmailLoaded, loadedGroupId, groupBusinessEmail]);



  useEffect(() => {

    if (groupEmailError) {

      toast.error(getErrorMessage(groupEmailError));

      dispatch(clearBusinessEmailError());

    }

  }, [groupEmailError, dispatch]);



  const linkDisabled =

    !domainVerified ||

    saving ||

    deleting ||

    statusLoading ||

    groupEmailLoading ||

    !resolvedGroupId;



  const handleLink = async () => {

    const value = emailInput.trim();

    if (!resolvedGroupId) {

      toast.error("Please select a group.");

      return;

    }

    if (!value) {

      toast.error("Please enter an email address.");

      return;

    }



    const result = await dispatch(

      patchBusinessEmail({ groupId: resolvedGroupId, business_email: value }),

    );

    if (patchBusinessEmail.fulfilled.match(result)) {

      setEmailInput(value);

      toast.success("Business email linked for this group.");

    }

  };



  const handleUnlink = async () => {

    if (!resolvedGroupId) return;



    const result = await dispatch(deleteBusinessEmail({ groupId: resolvedGroupId }));

    if (deleteBusinessEmail.fulfilled.match(result)) {

      setEmailInput("");

      toast.success("Business email unlinked for this group.");

    }

  };



  const showPageSkeleton = listLoading || (resolvedGroupId && !isGroupDataReady);



  return (

    <SettingsContent>

      <SettingsPageHeader

        title="Business Email Linking"

        description="Configure group-specific business emails for outbound replies"

      />



      {domainStatusReady && !domainVerified && !statusLoading ? (
      <Box sx={{ mt: "16px" }}>
          <DomainBanner />
        </Box>
      ) : null}



      <BusinessEmailInfoCard

        emailInput={emailInput}

        onEmailChange={setEmailInput}

        onLink={handleLink}

        linkDisabled={linkDisabled || showPageSkeleton || !isGroupDataReady}

        saveLoading={saving}

        isLinked={!showPageSkeleton && isGroupDataReady && isLinked}

        linkedEmail={groupBusinessEmail}

      />



      <BusinessEmailGroupSelect

        groups={activeGroups}

        value={resolvedGroupId}

        onChange={setSelectedGroupId}

        loading={listLoading}

      />



      {showPageSkeleton ? (

        <Box sx={{ mt: "16px", maxWidth: 632 }}>

          <Skeleton variant="rounded" height={80} sx={{ borderRadius: "8px" }} />

        </Box>

      ) : null}



      {!showPageSkeleton && isGroupDataReady && isLinked ? (

        <BusinessEmailVerifiedCard

          email={groupBusinessEmail}

          groupName={groupName}

          onUnlink={handleUnlink}

          saveLoading={saving || deleting}

        />

      ) : null}



      <BusinessEmailHierarchy hierarchy={hierarchy} />

    </SettingsContent>

  );

}



export default BusinessEmailPage;

